import { PdsAbstractInputWidget } from './pds-abstract-input-widget';
import { BasicValidationRules } from 'common-utils/interfaces';
import { RichTextEditorConfiguration, RichTextEditorConfigurationOptions } from './rich-text-editor-configurations';
import { pdsValidationRulesEngine } from './pds-validation-rules-engine';
import { pdsUtils } from './pds-utils';
import { pdsI18n } from './pds-i18n';
import { pdsLogger } from './pds-logger';

let log = pdsLogger.prependLogger(pdsLogger.requiresLogger('input-widgets'), 'pds-rich-text-editor.ts');

//TODO Import CKEDITOR rather than rely on CKEDITOR being a part of the global scope
declare let CKEDITOR;

export function handleCKEditorDialogDefinitionEvent(evt: any): void {
    // Remove the 'other' option from the link plugin
    // Check if the definition is from the dialog we're
    // interested in (the 'link' dialog).
    if (evt != null && evt.data != null && evt.data.name === 'link' && evt.data.definition != null) {
      let info = evt.data.definition.getContents('info');
      let protocol = info != null ? info.get('protocol') : null;
      if (protocol != null && protocol.items != null) {
        protocol.items = protocol.items.filter((item: any): boolean => {
          return item && item[1] !== '';
        });
      }
      //Below logic for making sure all inserted links in rich text editor will open in new tab.
      let informationTab = evt.data.definition.getContents('target');
      let targetField = informationTab != null ? informationTab.get('linkTargetType') : null;
      if (targetField != null) {
          targetField.default = '_blank';
        }
    }
  }

if (typeof CKEDITOR !== 'undefined' && CKEDITOR != null) {
  CKEDITOR.on('dialogDefinition', handleCKEditorDialogDefinitionEvent);

  /**
   * Mokey-Patching the setLang function. WARNING: It is generally not good practice to Monkey-Patch like this.
   * Make sure to explore other options and talk to other senior UI engineers before using techniques like
   * this.
   *
   * We monkey patch this function so that we can internationalize the text strings in the plugins used by
   * ckEditor.  Sadly, there was no hook provided in between when the message strings were downloaded, and when
   * the plugin would be initialized so we had to Monkey-Patch the function in order to provide the needed
   * hook.
   */
  const ckeSetLangFunction = CKEDITOR.plugins.setLang;
  CKEDITOR.plugins.setLang = function (a, b, c): void {
    pdsI18n.updateCKEditorMessageKeys(c, 'CKEDITOR.plugins.get(\'wordcount\').langEntries.en');
    ckeSetLangFunction.apply(CKEDITOR.plugins, arguments);
  };
}
export class PdsRichTextEditor extends PdsAbstractInputWidget {

  is = 'pds-rich-text-editor';
  editor: any;
  dataMaxlength: string;
  dataIsrequired: string;
  dataIsAdvanced: string;
  dataNoToolbar: string;
  dataCollapsibleToolbar: string;
  dataToolbarStartupExpanded: string;
  dataHideBottombar: string;
  dataShowCharcount: string;
  dataEnableInfoNotification: string;
  dataAutoFocus: string;
  dataAutoParagraph: string;
  dataShowSource: string;
  ignoreEditorChange: boolean;
  isAttached: boolean;
  // Used to store the initial modelValue in the case that the modelValue is set before the editor is initialized.
  initValue: string;
  dataPlainTextOnly: string;

  properties = {
    dataLabelText: String,
    dataShowLabel: {
      type: String,
      value: null
    },
    name: {
      type: String,
      value: ''
    },
    dataBadgeText: {
      type: String,
      value: '',
      observer: 'setUpBadge'
    },
    dataHideUnelevatedBadge: {
      type: String,
      value: 'false',
      observer: 'setUpBadge'
    },
    dataDisableInvalidBadgeElevation: {
      type: String,
      value: 'false',
      observer: 'setUpBadge'
    },
    dataIsrequired: {
      type: String,
      value: 'false',
      observer: 'setUpBadge'
    },
    dataMaxlength: {
      type: String,
      value: '500',
      observer: 'reinitializeEditor'
    },
    dataIsreadonly: String,
    dataNoToolbar: {
      type: String,
      value: 'false'
    },
    dataIsAdvanced: {
      type: String,
      value: 'false',
      observer: 'reinitializeEditor'
    },
    dataCollapsibleToolbar: {
      type: String,
      value: 'false'
    },
    dataHelperText: {
      type: String,
      value: null
    },
    dataTooltipText: String,
    dataUseAlternateColorScheme: {
      type: String,
      value: 'false'
    },
    dataToolbarStartupExpanded: {
      type: String,
      value: 'true'
    },
    dataHideBottombar: {
      type: String,
      value: 'false'
    },
    dataEnableInfoNotification: {
      type: String,
      value: 'false'
    },
    dataShowCharcount: {
      type: String,
      value: 'true' // Want this to default to true
    },
    dataAutoFocus: {
      type: String,
      value: 'false'
    },
    // dataAutoParagraph indicates whether to automatically create wrapping paragraph blocks around inline contents inside document body
    dataAutoParagraph: {
      type: String,
      value: 'true' //default value to be true
    },
    //dataShowSource indicates whether to show the Source button in the toolbar or not. When turned on, it allows all html tags except script tags.
    dataShowSource: {
      type: String,
      value: 'false'
    },
    dataPlainTextOnly: {
      type: String,
      value: 'false'
    }
  };

  reinitializeEditor(): void {
    log('reinitializeEditor() called');
    if (this.isAttached === true) {
      this.initializeEditor(this.dataIsAdvanced, this.dataNoToolbar, this.dataCollapsibleToolbar, this.dataToolbarStartupExpanded, this.dataHideBottombar, this.dataEnableInfoNotification, this.dataShowCharcount, this.dataAutoFocus, this.dataShowSource, this.dataAutoParagraph);
      this.setUpBadge();
    }
  }

  generateValidationRules(): void {
    let validationRules: BasicValidationRules = pdsValidationRulesEngine.getBasicValidationRules();

    validationRules.isRequired = (this.dataIsrequired === 'true');
    validationRules.isText = true;
    validationRules.setProperty('maxLength', this.dataMaxlength, Number);

    this.validators = pdsValidationRulesEngine.getValidatorsForRules(validationRules);

    if (this.hasOwnProperty('dataCustomvalidate')) {
      this.validators.push({
        execute: this.pds.validation.getCustomValidate(this.dataCustomvalidate),
        condition: {
          element: this
        }
      });
    }
  }

  showBadge(dataBadgeText: string): boolean {
    return (dataBadgeText != null && dataBadgeText.length > 0);
  }

  getConfiguration(options: RichTextEditorConfigurationOptions): any {
    log('getConfiguration() called');
    let configuration: any = RichTextEditorConfiguration.getConfiguration(options);
    configuration.wordcount.maxCharCount = this.dataMaxlength;
    return configuration;
  }

  attached(): void {
    log('attached() called');
    super.attached();
    this.isAttached = true;
    this.initializeEditor(this.dataIsAdvanced, this.dataNoToolbar, this.dataCollapsibleToolbar, this.dataToolbarStartupExpanded, this.dataHideBottombar, this.dataEnableInfoNotification, this.dataShowCharcount, this.dataAutoFocus, this.dataShowSource, this.dataAutoParagraph);
  }

  detached(): void {
    super.detached();
    this.isAttached = false;
  }

  onEditorChange(): void {
    log('onEditorChange() called');
    if (this.ignoreEditorChange) {
      // Turning this flag off here is for safeguard in case the two other places that
      // should turn this flag off do not happen. Not expecting but difficult to tell
      // if there are other circumstances when CKEditor will not call either of those
      // two places.
      this.ignoreEditorChange = true;
    } else {
      this.inputChange();
      this.validateWidget(true);
      this.dispatchInputEvent(this.querySelector(`#${this.name}-rich-text-editor`));
    }
  }

  initializeEditor(dataIsAdvanced: string, dataNoToolbar: string, dataCollapsibleToolbar: string, dataToolbarStartupExpanded: string, dataHideBottombar: string, dataEnableInfoNotification: string, dataShowCharcount: string, dataAutoFocus: string, dataShowSource: string, dataAutoParagraph: string): void {
    if (this.isAttached) {
      // Silly Polymer property parsing.  We want to only care if the parameters are actually strings.  During runtime, some of these
      // parameters can potentially be non-string.  This can happen if the attribute was defined in the UI as [dataShowCharcount]="true",
      // which gets evaluated as dataShowCharcount = true and not "true".  It should either be declared as data-show-charcount="true" or
      // [dataShowCharcount]="'true'" or [dataShowCharcount]="someBooleanVariable.toString()"
      let isOkayToProceed = this.areStrings(Array.from(arguments));
      if (isOkayToProceed) {
        let enableInfoNotification = (dataEnableInfoNotification === 'true');
        let options: RichTextEditorConfigurationOptions = {
          isReadOnly: (this.dataIsreadonly === 'true'),
          showToolbar: (dataNoToolbar !== 'true'),
          collapsibleToolbar: (dataCollapsibleToolbar === 'true'),
          toolbarStartupExpanded: (dataToolbarStartupExpanded === 'true'),
          showBottomBar: (dataHideBottombar !== 'true'),
          showCharCount: (dataShowCharcount === 'true') && (dataHideBottombar !== 'true'),
          useAdvancedMode: (dataIsAdvanced === 'true'),
          startupFocus: (dataAutoFocus === 'true'),
          showSource: (dataShowSource === 'true'),
          autoParagraph: (dataAutoParagraph === 'true')
        };
        let configuration = this.getConfiguration(options);

        if (this.editor != null) {
          let data = this.modelValue;
          this.editor.focusManager.blur(true);
          this.editor.destroy(true);
          this.editor = CKEDITOR.replace(this.name + '-rich-text-editor', configuration);
          this.editor.setData(data);
        } else {
          /**
           * Since we are initializing the editor here, we need to initialize the modelValue from
           * the stored value in initValue since the modelValue setter has already fired.
           */
          this.editor = CKEDITOR.replace(this.name + '-rich-text-editor', configuration);
          this.editor.setData(this.initValue);

          // the intialValue has been used - clear it out to prevent it from accidently used again.
          delete this.initValue;
        }

        this.editor.on('mode', () => {
          if (this.editor.mode === 'source') {
            let editable: any = this.editor.editable();
            // the "editable" object above seems to be destroyed and recreated everytime when the mode is switched.
            // hence, attaching the listener below doesn't attach multiple listeners to the same object.
            editable.attachListener(editable, 'blur', () => {
              this.focusLost(new Event('blur'));
            });
            // according to ckeditor documentation, the change event is only fired in the wysiwyg mode.
            // because we need the change in model value on every input, a listener needs to be attached separately for source mode.
            // ref: https://ckeditor.com/docs/ckeditor4/latest/api/CKEDITOR_editor.html#event-change
            editable.attachListener(editable, 'input', () => {
              this.onEditorChange();
            });
          }
        });

        this.editor.on('change', () => {
          this.onEditorChange();
        });

        this.editor.on('instanceReady', () => {
          // There is a case when setModelValue gets called and then a new instance gets created calling setModelValue as well.
          // When this happens, the first call to setModelValue sets up the ignoreEditorChange to true but never gets turned off
          // because the callback that was set up no longer gets called.  Therefore, we need to turn it off here.  This scenario
          // is very specific to the score inspector with the comment editor in full mode gets used.  In the score inspector's
          // comment editor "mini-mode", this case does not happen.
          this.ignoreEditorChange = false;
        });

        this.editor.on('blur', () => {
          this.focusLost(new Event('blur'));
        });

        this.editor.on('focus', () => {
          this.dispatchEvent(new Event('focus'));
        });

        this.editor.on('langLoaded', () => {
          log('ck editor lang loaded');
          pdsI18n.updateCKEditorMessageKeys(CKEDITOR.lang.en, 'CKEDITOR.lang.en');
        });

        // Supressing notification reference: http://docs.ckeditor.com/#!/guide/dev_notifications
        // The CKEditor informational notification message may appear like an error and just creates confusion
        // whether the message is an error or not.  A good example is that when you copy-paste url into CKEditor,
        // CKEditor will try to see if it can embed its content.  If it cannot, then it leaves the URL as pasted.
        // However, you will be given this notification "The URL could not be automatically embedded".  It's not
        // an error but CKEditor is just letting you know.  CPF-3750 is to supress this message to avoid confusion.
        this.editor.on('notificationShow', (evt) => {
          if (!enableInfoNotification && evt.data.notification.type === 'info') {
            evt.cancel();
          }
        });
        this.editor.on('notificationUpdate', (evt) => {
          if (!enableInfoNotification && evt.data.notification.type === 'info') {
            // Note that this does NOT cancel the update.  Just the notification.  See the reference doc above.
            evt.cancel();
          }
        });
      }
    }
  }

  get value(): any {
    if (this.editor != null) {
      //NOTE: Most, if not always, HTML tags are required to be retained as part of rich text editor value
      return (this.dataPlainTextOnly === 'true') ? pdsUtils.stripHtml(this.editor.getData()) : this.editor.getData();
    }
  }

  /**
   * Overridden because because this widget does not use an <input>.
   */
  get modelValue(): any {
    if (this.editor != null) {
      //NOTE: Most, if not always, HTML tags are required to be retained as part of rich text editor value
      return (this.dataPlainTextOnly === 'true') ? pdsUtils.stripHtml(this.editor.getData()) : this.editor.getData();
    } else {
      // if the editor is missing, return the init value as the model value
      return this.initValue;
    }
  }

  /**
   * Identical to the super function, but copied here because deleting it resulted in a lot of errors.
   * I think there must have been an issue with inheritance and get/set pairs.
   */
  set modelValue(value) {
    log('set modelValue called. value: ' + value);

    /**
     * We need to ignore the synchronous 'change' event that the CKEDITOR will fire
     * because it will throw errors in Angular 2+. We only need to handle the change events
     * when the changes are coming directly from the CKEDITOR anyway.
     */
    if (this.editor == null) {
      /**
       * If the DOM attempts to set the modelValue before the editor is initialized we need to store the
       * value parameter, that way when the editor is initialized we can set the proper value,
       * rather than an empty string.
       */
      this.initValue = value;
    }
    this.ignoreEditorChange = true;
    let modelValueChanged = this.setModelValue(value);
    if (modelValueChanged) {
      this.validateWidget();
    }
  }

  /**
   *
   * @param value - The string the user typed into the input.
   * @param skip - skips setting the model value. (Keeps from getting into recursive loops)
   */
  setValue(value: string, skip = false): void {
    log('setValue called. value: ' + value + ' skip: ' + skip);

    if (value == null) {
      value = '';
    }
    //Stripping HTML from editor's data, when property is set, before comparision with value to avoid recursive loop
    if (this.editor != null && value !== ((this.dataPlainTextOnly === 'true') ? pdsUtils.stripHtml(this.editor.getData()) : this.editor.getData())) {
      this.editor.setData(value);
    }

    if (skip !== true) {
      this.setModelValue(value, true);
    }
  }

  /**
   * @param modelValue
   * @param skip
   * @returns returns if the model value was actually set.
   */
  setModelValue(modelValue: string, skip?: boolean): boolean {
    log('setModelValue called. modelValue: ' + modelValue + ' skip: ' + skip);

    //Stripping HTML from editor's data, when property is set, before comparision with value to avoid recursive loop
    if (this.editor != null && modelValue !== ((this.dataPlainTextOnly === 'true') ? pdsUtils.stripHtml(this.editor.getData()) : this.editor.getData())) {
      // See https://ckeditor.com/docs/ckeditor4/latest/api/CKEDITOR_editor.html#method-setData.
      try {
        this.editor.setData(modelValue, {
          callback: (): void => {
            // This callback gets called after setData() has completed.  setData() will fire the change event before it completes, which will cause
            // the "inputChange()" function to execute when this.ignoreEditorChange is false.  When setModelValue gets called with this.ignoreEditorChange
            // set to true, we do not want to prematurely turn this flag off anywhere else because it may cause the inputChange() function to get executed,
            // which is not the desired behavior.  Therefore, turn this flag off inside this callback to ensure the proper behavior.
            this.ignoreEditorChange = false;
          }
        });
      } catch (e) {
        this.ignoreEditorChange = false;
      }
      if (skip !== true) {
        this.setValue(modelValue, true);
      }
      return true;
    } else {
      this.ignoreEditorChange = false;
    }
    return false;
  }

  private areStrings(values: Array<any>): boolean {
    for (let value of values) {
      if (typeof value !== 'string') {
        return false;
      }
    }
    return true;
  }
}
